import { Fragment, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LEGAL_DOCS, legalDoc, type LegalEntity } from '@wulftime/core';
import { Logo } from '../components/logo';
import { Alert, Spinner } from '../components/ui';

export interface LegalInfo {
  entity: LegalEntity;
  complete: boolean;
  documents: { slug: string; title: string; version: string; requiresAcceptance: boolean }[];
}

export const useLegalInfo = () => useQuery({ queryKey: ['public-legal'], queryFn: () => fetch('/api/v1/public/legal').then((r) => r.json() as Promise<LegalInfo>), staleTime: 60_000 });

/** Negrita y enlaces dentro de una línea de texto. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={m.index}>{m[1]}</strong>);
    else if (m[3]!.startsWith('/'))
      out.push(
        <Link key={m.index} to={m[3]!} className="text-brand-700 underline">
          {m[2]}
        </Link>,
      );
    else
      out.push(
        <a key={m.index} href={m[3]} className="text-brand-700 underline" target="_blank" rel="noreferrer">
          {m[2]}
        </a>,
      );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  // Los datos que faltan («[pendiente: …]») se resaltan para que se vean
  return out.flatMap((n, i) =>
    typeof n === 'string'
      ? n.split(/(\[pendiente:[^\]]*\])/).map((part, j) =>
          part.startsWith('[pendiente:') ? (
            <mark key={`p${i}-${j}`} className="rounded bg-amber-100 px-1 text-amber-900">
              {part.slice(1, -1)}
            </mark>
          ) : (
            <Fragment key={`t${i}-${j}`}>{part}</Fragment>
          ),
        )
      : [<Fragment key={`n${i}`}>{n}</Fragment>],
  );
}

/** Markdown sencillo de los textos legales: títulos (##), listas (-) y párrafos. */
export function LegalText({ markdown }: { markdown: string }) {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={`l${blocks.length}`} className="my-3 list-disc space-y-1.5 pl-5">
          {list.map((li, i) => (
            <li key={i}>{inline(li)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };
  for (const raw of markdown.split('\n')) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (line.startsWith('## ')) {
      flush();
      blocks.push(
        <h2 key={`h${blocks.length}`} className="mt-8 text-lg font-semibold text-brand-950">
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith('- ')) list.push(line.slice(2));
    else {
      flush();
      blocks.push(
        <p key={`p${blocks.length}`} className="my-3">
          {inline(line)}
        </p>,
      );
    }
  }
  flush();
  return <div className="text-[15px] leading-relaxed text-slate-700">{blocks}</div>;
}

/** Enlaces a los textos legales (pie de la portada, alta, inicio de sesión). */
export function LegalLinks({ className = '' }: { className?: string }) {
  return (
    <nav aria-label="Información legal" className={`flex flex-wrap justify-center gap-x-4 gap-y-1 ${className}`}>
      {LEGAL_DOCS.map((d) => (
        <Link key={d.slug} to={`/legal/${d.slug}`} className="hover:text-brand-700 hover:underline">
          {d.title}
        </Link>
      ))}
    </nav>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const doc = legalDoc(slug ?? '');
  const { data, isLoading } = useLegalInfo();
  if (!doc) return <Navigate to="/legal/aviso-legal" replace />;
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4">
          <Link to="/" aria-label="WulfTime, inicio">
            <Logo />
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-brand-700">
            Volver a la portada
          </Link>
        </div>
      </header>
      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-10 md:grid-cols-[220px_1fr]">
        <aside>
          <nav aria-label="Documentos legales" className="sticky top-6 space-y-1 text-sm">
            {LEGAL_DOCS.map((d) => (
              <Link key={d.slug} to={`/legal/${d.slug}`} className={`block rounded-lg px-3 py-2 ${d.slug === doc.slug ? 'bg-brand-50 font-medium text-brand-800' : 'text-slate-600 hover:bg-slate-50'}`}>
                {d.title}
              </Link>
            ))}
          </nav>
        </aside>
        <main>
          <h1 className="text-3xl font-bold tracking-tight text-brand-950">{doc.title}</h1>
          <p className="mt-1 text-sm text-slate-500">Versión del {new Date(`${doc.version}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          {isLoading || !data ? (
            <Spinner />
          ) : (
            <>
              {!data.complete && (
                <Alert tone="warning" className="mt-4">
                  Faltan datos del titular del servicio (marcados en amarillo). Se completarán en cuanto esté constituida la sociedad.
                </Alert>
              )}
              <LegalText markdown={doc.body(data.entity)} />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
